import React from "react"
import { useShallow } from "zustand/react/shallow"

// components
import { Affix, Typography } from "antd"
import RegistryButtons from "../RegistryButtons/RegistryButtons"
import RightActions from "./components/RightActions/RightActions"

// utils/helpers
import { getSectionRegistry } from "utilities/study-views"
import { formSchemas, SCROLLABLE_AUTHORING_CONTAINER } from "constants/authoring"

// hooks
import { useGetAuthoringStudy } from "api/hooks/studies/useAuthoringApi"
import useAuthoringViewStore from "../../hooks/stores/useAuthoringViewStore"
import useAuthoringValidationsStore from "../../hooks/stores/useAuthoringValidationsStore"
import useAuthoringStore from "../../hooks/stores/useAuthoringStore"
import useAuthoringTranslations from "../../hooks/stores/useAuthoringTranslations"
import useAuthoringValidationActions from "../../hooks/actions/useAuthoringValidationActions"

import "./FormActions.scss"

const { Title, Text } = Typography

const FormActions = ({ formDataRef }) => {
  const { formView, setFormView, formSection, fullScreen, setShowUniqueRegistryPreviewType, showPagePreview } =
    useAuthoringViewStore(useShallow((state) => state))
  const { clearValidations } = useAuthoringValidationActions()

  const [showStudyId, setShowStudyId] = React.useState(false)

  const { setIsTranslationActive, setTranslationLanguages, setSelectedLanguage } = useAuthoringTranslations(
    useShallow((state) => ({
      setIsTranslationActive: state.setIsTranslationActive,
      setTranslationLanguages: state.setTranslationLanguages,
      setSelectedLanguage: state.setSelectedLanguage,
    }))
  )

  const { authoringId } = useAuthoringStore(
    useShallow((state) => ({
      authoringId: state.authoringId,
    }))
  )

  const { registryValidations } = useAuthoringValidationsStore(
    useShallow((state) => ({
      registryValidations: state.registryValidations,
    }))
  )

  const { data: authoring } = useGetAuthoringStudy(authoringId)

  // when switch sections set registry view
  React.useEffect(() => {
    setFormView(getSectionRegistry(formSection, formView))
  }, [formSection])

  React.useEffect(() => {
    if (formView?.id === "jrct") {
      setTranslationLanguages(["jp"])
      setSelectedLanguage("jp")
    } else {
      setTranslationLanguages([])
      setSelectedLanguage(null)
      setIsTranslationActive(false)
    }

    if (formView?.id !== "eudra") {
      setShowUniqueRegistryPreviewType(null)
    }
  }, [formView?.id])

  const validationErrorsExist = React.useMemo(() => {
    if (registryValidations === null) return null
    let shouldBreak = false

    if (registryValidations) {
      Object.keys(registryValidations).forEach((key) => {
        registryValidations[key].forEach((validation) => {
          if (shouldBreak) return
          if (validation.type === "ERROR") {
            shouldBreak = true
            return
          }
        })
        if (shouldBreak) return
      })
    }

    return shouldBreak
  }, [registryValidations])

  let classNames = [`study-data-form-actions`]

  if (validationErrorsExist) classNames.push("form-action-validation-error")
  if (validationErrorsExist === false) classNames.push("form-action-validation-no-error")
  if (fullScreen) classNames.push("form-actions-full-screen")

  return (
    <Affix
      target={() => document.getElementsByClassName(SCROLLABLE_AUTHORING_CONTAINER.FORM_CLASSNAME)[0]}
      offsetTop={0}
      style={{ zIndex: 3 }}
      onChange={(affixed) => {
        setShowStudyId(affixed)
      }}
    >
      <div className={classNames.join(" ")}>
        <div className="registry-actions-container">
          {(fullScreen || showStudyId) && (
            <>
              <Title level={4}>{authoring?.study_id}</Title>
              {fullScreen && <Text>|</Text>}
            </>
          )}
          {fullScreen && (
            <>
              <span className="registry-label">Registries</span>
              <RegistryButtons
                formView={formView}
                setFormView={setFormView}
                formSection={formSection}
                formSchemas={formSchemas}
                showPagePreview={showPagePreview}
                validationErrorsExist={validationErrorsExist}
                clearValidations={clearValidations}
              />
            </>
          )}
        </div>
        {formView?.id && authoring?.id && <RightActions formDataRef={formDataRef} />}
      </div>
    </Affix>
  )
}

export default FormActions
